import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { AuthContext } from '../../AuthContext';
import PageServices from '../../services/PageServices';
import CalendarPicker from './CalendarPicker';
import stockList from '../../stock.json';

// Power Stocks (AOC style): for the selected day, the stocks whose option chain had all three
// 100% marks — OI, OI change and volume — on one strike right next to spot. PE side →
// Support, CE side → Resistance (GET /powerStocks?date=). Each stock lists every time it
// happened; "Historical" opens the Backtester at that moment, "Live" the live option chain.

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const LOOKBACK_DAYS = 90;
const TODAY_REFRESH_MS = 60 * 1000;

const istNow = () => new Date(Date.now() + 330 * 60 * 1000); // read with UTC getters
const fmtDate = (d) => `${String(d.getUTCDate()).padStart(2, '0')}-${MONTHS[d.getUTCMonth()]}-${d.getUTCFullYear()}`;

// Weekdays from today back LOOKBACK_DAYS (the calendar's selectable dates)
const tradingDays = () => {
  const now = istNow();
  const start = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const days = [];
  for (let back = 0; back <= LOOKBACK_DAYS; back++) {
    const d = new Date(start - back * 86400000);
    if (d.getUTCDay() !== 0 && d.getUTCDay() !== 6) days.push(fmtDate(d));
  }
  return days;
};

const ist = (epochSec) => new Date((epochSec + 330 * 60) * 1000);
const fmtClock = (epochSec) => {
  const d = ist(epochSec);
  const h = d.getUTCHours();
  return `${String(h % 12 || 12).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}${h < 12 ? 'AM' : 'PM'}`;
};
const fmtTimeParam = (epochSec) => {
  const d = ist(epochSec);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}:59`;
};
// "21hours ago" within a day, else the date (as AOC)
const fmtWhen = (epochSec) => {
  const ageSec = Date.now() / 1000 - epochSec;
  if (ageSec < 3600) return `${Math.max(1, Math.floor(ageSec / 60))}min ago`;
  if (ageSec < 86400) return `${Math.floor(ageSec / 3600)}hours ago`;
  const d = ist(epochSec);
  return `${String(d.getUTCDate()).padStart(2, '0')}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${d.getUTCFullYear()}`;
};

// Select a symbol the same way the header search does, then open the page in a new tab
// (localStorage is shared, so the new tab loads with this symbol).
const openSymbol = (symbol, url) => {
  const lot = stockList.find((s) => s.name === symbol)?.lotSize;
  localStorage.setItem('stock', symbol);
  if (lot) localStorage.setItem('expiry', lot);
  window.open(url, '_blank', 'noopener,noreferrer');
};
const historicalUrl = (symbol, date, epochSec) =>
  `/backtester?date=${encodeURIComponent(date)}&time=${encodeURIComponent(fmtTimeParam(epochSec))}&symbolToken=${encodeURIComponent(symbol)}`;

const SIDES = {
  support: { title: 'Support', color: '#2e9d4f' },
  resistance: { title: 'Resistance', color: '#d64545' },
};

const StockCard = ({ item, side, date }) => {
  const [open, setOpen] = useState(false);
  const color = SIDES[side].color;
  const latest = item.hits[0];
  const btn = { fontSize: 14, padding: '4px 14px', borderRadius: 8, whiteSpace: 'nowrap' };

  return (
    <div className="card mb-2 shadow-sm" style={{ borderLeft: `4px solid ${color}` }}>
      <div className="card-body py-2 px-3 d-flex align-items-center justify-content-between gap-2 flex-wrap">
        <div>
          <div className="d-flex align-items-center gap-2">
            <strong>{item.symbol}</strong>
            {item.count > 1 && (
              <button
                type="button"
                className="badge border-0"
                style={{ background: `${color}22`, color, cursor: 'pointer' }}
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                title={open ? 'Hide times' : 'Show all times'}
              >
                ×{item.count} {open ? '▴' : '▾'}
              </button>
            )}
          </div>
          <small className="text-muted">
            {fmtClock(latest.time)} · {fmtWhen(latest.time)} · strike {latest.strike}
          </small>
        </div>
        <div className="d-flex gap-2">
          <button type="button" className="btn btn-outline-secondary" style={btn} onClick={() => openSymbol(item.symbol, historicalUrl(item.symbol, date, latest.time))}>
            Historical
          </button>
          <button type="button" className="btn btn-primary" style={btn} onClick={() => openSymbol(item.symbol, '/optionchain')}>
            Live
          </button>
          {/* Daily Trend (as the header's DAILY): ↑ Bullish, ↓ Bearish */}
          {(item.dailyTrend === 'Bullish' || item.dailyTrend === 'Bearish') && (
            <span
              className="align-self-center fw-bold"
              style={{ color: item.dailyTrend === 'Bullish' ? '#2e9d4f' : '#d64545', fontSize: 22, lineHeight: 1 }}
              title={`Daily trend: ${item.dailyTrend}`}
              aria-label={`Daily trend: ${item.dailyTrend}`}
            >
              {item.dailyTrend === 'Bullish' ? '↑' : '↓'}
            </span>
          )}
        </div>
      </div>
      {open && (
        <ul className="list-unstyled mb-0 px-3 pb-2 border-top">
          {item.hits.map((hit) => (
            <li key={hit.time} className="d-flex justify-content-between align-items-center pt-2">
              <small>
                <i className="fa fa-clock-o me-2" />
                {fmtClock(hit.time)} · {fmtWhen(hit.time)} · strike {hit.strike} · spot {hit.spot}
              </small>
              <button
                type="button"
                className="btn btn-link p-0"
                style={{ fontSize: 14 }}
                onClick={() => openSymbol(item.symbol, historicalUrl(item.symbol, date, hit.time))}
              >
                Historical
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Column = ({ side, items, date, loading }) => (
  <div className="col-12 col-lg-6 mb-3">
    <h5 className="d-flex align-items-center gap-2 mb-3">
      <span style={{ width: 10, height: 10, borderRadius: '50%', background: SIDES[side].color, display: 'inline-block' }} />
      {SIDES[side].title} <span className="text-muted">{items.length}</span>
    </h5>
    {items.map((item) => <StockCard key={item.symbol} item={item} side={side} date={date} />)}
    {!items.length && !loading && <p className="text-muted text-center py-5">No {SIDES[side].title} stocks</p>}
  </div>
);

const PowerStocks = () => {
  const { isLoggedIn } = useContext(AuthContext);
  const days = useMemo(tradingDays, []);
  const [date, setDate] = useState(days[0]);
  const [search, setSearch] = useState('');
  const [result, setResult] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ready | error

  useEffect(() => {
    let cancelled = false;
    const load = (first) => {
      if (first) {
        setStatus('loading');
        setResult(null);
      }
      PageServices.getPowerStocks(date)
        .then((res) => {
          if (cancelled) return;
          setResult(res?.data || null);
          setStatus('ready');
        })
        .catch(() => {
          if (!cancelled && first) setStatus('error');
        });
    };
    load(true);
    const id = date === days[0] ? setInterval(() => load(false), TODAY_REFRESH_MS) : null; // today keeps updating
    return () => {
      cancelled = true;
      if (id) clearInterval(id);
    };
  }, [date, days]);

  const filter = (list) => {
    const q = search.trim().toUpperCase();
    return (list || []).filter((s) => !q || s.symbol.includes(q));
  };

  if (!isLoggedIn) return <Navigate to="/" />;

  return (
    <section className="container-fluid py-3 ps-page">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
        <div className="d-flex align-items-center gap-3">
          {/* The site's sidebar toggle (custom.js opens #mySidenav for any .toggle-btn-outer button);
              this page has no top header. */}
          <div className="toggle-btn-outer" style={{ width: 'auto' }}>
            <button type="button" aria-label="Open menu">
              <i className="fa fa-bars" />
            </button>
          </div>
          <h3 className="mb-0">Power Stocks</h3>
        </div>
        <div style={{ minWidth: 220 }}>
          <CalendarPicker
            value={date}
            dates={days}
            onChange={setDate}
            placeholder="Select Date"
            dialogLabel="Choose date"
            emptyText="No dates"
            align="right"
          />
        </div>
      </div>

      <input
        type="search"
        className="form-control form-control-lg mb-3"
        placeholder="Search symbol"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {status === 'loading' && <p className="text-center text-muted py-4">Scanning option chains for {date}…</p>}
      {status === 'error' && <p className="text-center text-danger py-4">Could not load Power Stocks. Please try again.</p>}
      {status === 'ready' && result && (
        <p className="text-muted small mb-3">
          {result.scannedStocks} stocks with option data during market hours on {date}
          {result.failedStocks?.length ? ` · ${result.failedStocks.length} could not be read` : ''}
        </p>
      )}

      <div className="row">
        <Column side="support" items={filter(result?.support)} date={date} loading={status === 'loading'} />
        <Column side="resistance" items={filter(result?.resistance)} date={date} loading={status === 'loading'} />
      </div>
    </section>
  );
};

export default PowerStocks;
